import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debounce } from "es-toolkit";

import { isEqualTimeframe } from "../components/Chart/settings";
import { TimeFrameType } from "../resources/types";

const CHANNEL_NAME = "metrics_pinned_tabs_sync";

const chanEvent = {
  SYNC_TIMEFRAME: "SYNC_TIMEFRAME",
  REQUEST_PIN_LIST: "REQUEST_PIN_LIST",
  SEND_PIN_LIST: "SEND_PIN_LIST"
};

type TimeframeSyncProps = {
  setTimeFrame: (p: TimeFrameType) => void;
  timeFrame: TimeFrameType;
};

type ConnectedPins = {
  syncId: string;
  timeframe: TimeFrameType;
  timestamp: number;
};

/**
 * Hook to synchronize pinned tabs across browser instances.
 * @param props - Configuration for timeframe synchronization
 * @returns Methods and state for managing sync
 */
const usePinnedTabsSync = ({ setTimeFrame, timeFrame }: TimeframeSyncProps) => {
  const [synced, setSynced] = useState(false);
  const [syncId, setSyncId] = useState<string | null>(null);
  const [connectedPins, setConnectedPins] = useState<ConnectedPins[]>([]);
  const chan = useMemo(() => new BroadcastChannel(CHANNEL_NAME), []);
  const exitPinRef = useRef({ synced: false, connectedPins, syncId });

  const getIsLeaderPin = useCallback(
    (s = synced, id = syncId, pins = connectedPins) => {
      // a leader pin is the oldest pinned tab this pin is elected by itself on condition
      // that its timestamp is the oldest, this pin is the costodians of every other tabs
      // in synchronization
      if (!s) return false;
      const pin = pins.find(p => p.syncId === id);
      return pin && pins.every(p => p.timestamp >= pin.timestamp);
    },
    [connectedPins, syncId, synced]
  );

  const debouncedTimeframeSync = debounce((timeframe: TimeFrameType) => {
    chan.postMessage({ type: chanEvent.SYNC_TIMEFRAME, timeframe });
  }, 1);

  useEffect(() => {
    if (synced) {
      debouncedTimeframeSync(timeFrame);
    }
    return () => {
      debouncedTimeframeSync.cancel();
    };
  }, [timeFrame, synced, debouncedTimeframeSync]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!event.data) return;
      const { type } = event.data;
      const syncTimeframe = event.data.timeframe;
      const isLeader = getIsLeaderPin();

      switch (type) {
        case chanEvent.SYNC_TIMEFRAME: {
          const equalTimeframe = isEqualTimeframe(timeFrame, syncTimeframe);
          if (synced && event.data.syncId !== syncId && !equalTimeframe) {
            setTimeFrame(syncTimeframe);
          }
          break;
        }
        case chanEvent.REQUEST_PIN_LIST:
          //respond to the request of list of connected pins to be broadcasted
          if (isLeader) {
            chan.postMessage({
              type: chanEvent.SEND_PIN_LIST,
              syncId,
              connectedPins
            });
          }

          break;
        case chanEvent.SEND_PIN_LIST:
          //receive list of connected pins
          !isLeader && setConnectedPins(event.data.connectedPins);
          break;
      }
    },
    [
      chan,
      connectedPins,
      getIsLeaderPin,
      setTimeFrame,
      syncId,
      synced,
      timeFrame
    ]
  );

  useEffect(() => {
    chan.onmessage = handleMessage;
  }, [chan, handleMessage]);

  useEffect(
    //request pin list on mount
    () => {
      chan.postMessage({ type: chanEvent.REQUEST_PIN_LIST });
    },
    [chan]
  );

  const handleExitUnload = useCallback(() => {
    const connectedPins = exitPinRef.current.connectedPins;
    const syncId = exitPinRef.current.syncId;

    const updatedPins = connectedPins.filter(p => p.syncId !== syncId);
    chan.postMessage({
      type: chanEvent.SEND_PIN_LIST,
      connectedPins: updatedPins,
      syncId
    });

    chan.onmessage = null;
    chan.close();
  }, [chan]);

  useEffect(() => {
    exitPinRef.current = { syncId, synced, connectedPins };
    window.addEventListener("beforeunload", handleExitUnload);
    return () => {
      window.removeEventListener("beforeunload", handleExitUnload);
    };
  }, [connectedPins, handleExitUnload, syncId, synced]);

  useEffect(
    () => handleExitUnload,
    // No deps array to prevent early unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onSyncChange = (sync: boolean, timeframe: TimeFrameType) => {
    const timestamp = Date.now();
    setSynced(sync);

    if (sync) {
      //Add this sync and broadcast a new added sync to channels
      const id = crypto.randomUUID();
      setSyncId(id);
      const updatedPins = [
        ...connectedPins,
        { syncId: id, timeframe, timestamp }
      ];
      chan.postMessage({
        type: chanEvent.SEND_PIN_LIST,
        syncId,
        connectedPins: updatedPins
      });
      setConnectedPins(updatedPins);
      const lastPin = connectedPins.reduce((p, c) =>
        p.timestamp > c.timestamp ? p : c
      );
      setTimeFrame(lastPin.timeframe);
    } else {
      setSyncId(null);
      const updatedPins = connectedPins
        .filter(p => p.syncId !== syncId)
        .map(p => ({ ...p, timeframe }));
      setConnectedPins(updatedPins);
      chan.postMessage({
        type: chanEvent.SEND_PIN_LIST,
        connectedPins: updatedPins,
        syncId
      });
    }
  };

  const canJoinSync = !synced && connectedPins.length > 0;

  return { onSyncChange, isSynced: synced, canJoinSync };
};

export default usePinnedTabsSync;
